import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { requireAppCheck } from '@jfw/angular/shared/app-check';
import { PLATFORM_DP_ENVIRONMENT } from '@jfw/angular/shared/tokens';
import {
  ActiveShippingHistoryEntry,
  GetOrderShippingOptionsResponse,
  GetShippingDispositionResponse,
  ShippingInquiry,
  ShippingInquiryResponse,
  WholesaleOrder,
} from '@jfw/types';
import { catchError, map, Observable, of, retry } from 'rxjs';

type ShippingInquiryResult = {
  success: boolean;
  data: ShippingInquiryResponse[];
};

@Injectable({
  providedIn: 'root',
})
export class WholesaleShippingService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(PLATFORM_DP_ENVIRONMENT);
  private readonly apiUrl = this.environment.wholesale_shipping_api_url;

  private readonly reqHeader = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  public shippingInquiry(
    request: ShippingInquiry,
  ): Observable<ShippingInquiryResult> {
    return this.http
      .post<ShippingInquiryResponse[]>(
        `${this.apiUrl}/shipping-inquiry`,
        request,
        {
          ...requireAppCheck,
          headers: this.reqHeader,
        },
      )
      .pipe(
        map((data) => ({
          success: true,
          data,
        })),
        catchError((error) => {
          console.error(error);
          return of({ success: false, data: [] });
        }),
      );
  }

  // Cheap disposition-only check — call this first and only fall through to
  // getOrderShippingOptions() when it comes back CanDeliver, so the common
  // "plenty of time" and blocking cases never pay for the full priced-option
  // computation.
  public getShippingDisposition(
    order: WholesaleOrder,
  ): Observable<GetShippingDispositionResponse> {
    return this.http
      .post<GetShippingDispositionResponse>(
        `${this.apiUrl}/shipping-disposition`,
        { order },
        {
          ...requireAppCheck,
          headers: this.reqHeader,
        },
      )
      .pipe(
        // EXT-5999: a single transient failure (e.g. a Cloud Run cold start
        // on platform-order, the only container hosting ShippingService)
        // used to get cached by order-entry.component.ts's eager prefetch
        // and replayed on every subsequent Submit click until an unrelated
        // form edit invalidated the cache (see that file's own comment).
        // Retry before surfacing a hard failure, so the common transient
        // case resolves within this same call -- same idiom already used for
        // this bug class in event.service.ts's getWarehouseByAccountAndDate
        // (EXT-5980).
        retry({ count: 2, delay: 1000 }),
        catchError((error) => {
          console.error(error);
          return of({
            success: false,
            error: error?.message ?? 'Failed to determine shipping disposition',
          });
        }),
      );
  }

  public getOrderShippingOptions(
    order: WholesaleOrder,
  ): Observable<GetOrderShippingOptionsResponse> {
    return this.http
      .post<GetOrderShippingOptionsResponse>(
        `${this.apiUrl}/calculate-freight`,
        { order },
        {
          ...requireAppCheck,
          headers: this.reqHeader,
        },
      )
      .pipe(
        catchError((error) => {
          console.error(error);
          return of({
            success: false,
            error: error?.message ?? 'Failed to retrieve shipping options',
          });
        }),
      );
  }

  // EXT-5141: active prior shipping-carrier selections for this account's
  // other orders in the same production week, surfaced in the Shipping
  // Options Modal so a CSR can match a choice already made on a sibling
  // order for the same event.
  public getActiveShippingHistory(
    accountNumber: string,
    productionWeek: string,
  ): Observable<ActiveShippingHistoryEntry[]> {
    return this.http
      .get<ActiveShippingHistoryEntry[]>(
        `${this.apiUrl}/active-shipping-history/${accountNumber}/${productionWeek}`,
        {
          ...requireAppCheck,
          headers: this.reqHeader,
        },
      )
      .pipe(
        catchError((error) => {
          console.error(error);
          return of([]);
        }),
      );
  }
}
